// Helpers for unwrapping supabase-js query results.
//
// supabase-js does NOT throw on query failures — errors come back in the
// `error` field of the result object. Code that ignores that field (or wraps
// the call in try/catch expecting a throw) silently masks failures, e.g.
// stats rendering as zeros when a query is rejected by RLS or a typo.
// These helpers convert result errors into thrown Errors so callers can
// propagate them to their hook's error state.

interface SupabaseResultError {
  message: string;
}

/**
 * Unwrap a supabase result, throwing on error and returning non-null data.
 * Do NOT use with `.maybeSingle()` (where `data: null` is a legitimate
 * "not found" result) — use {@link unwrapMaybe} instead.
 */
export function unwrap<T>(res: { data: T | null; error: SupabaseResultError | null }): T {
  if (res.error) throw new Error(res.error.message);
  if (res.data === null) throw new Error('Supabase zwróciło pustą odpowiedź (data=null)');
  return res.data;
}

/** Unwrap a supabase result where `data: null` is a valid outcome (e.g. `.maybeSingle()`). */
export function unwrapMaybe<T>(res: {
  data: T | null;
  error: SupabaseResultError | null;
}): T | null {
  if (res.error) throw new Error(res.error.message);
  return res.data;
}

/** Unwrap a count query result (`{ count: 'exact', head: true }`), throwing on error. */
export function unwrapCount(res: {
  count: number | null;
  error: SupabaseResultError | null;
}): number {
  if (res.error) throw new Error(res.error.message);
  return res.count ?? 0;
}
